import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { environment } from '../../../environments/environments';
import { USE_ADMIN_API } from './api.context';
import { USE_OTP_API } from './api.context';
import { SUPPRESS_ERROR_TOAST } from './api.context';
import { AuthService } from '../services/auth/auth.service';
import { ToastService } from '../services/toast/toast.service';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const toast = inject(ToastService);

  // 1. Context-Based URL Routing
  const useAdminApi = req.context.get(USE_ADMIN_API);
  const useOTPApi = req.context.get(USE_OTP_API);
  const suppressErrorToast = req.context.get(SUPPRESS_ERROR_TOAST);
  const baseUrl = useAdminApi 
    ? environment.admin.api_base_url 
    : useOTPApi 
      ? environment.otp.otp_base_url 
      : environment.ming_client.api_base_url;
  // 2. Token Retrieval
  const token = localStorage.getItem('authToken');

  // 3. Request Cloning (Base URL + Headers)
  // For FormData requests, do NOT set Content-Type so browser sets multipart/form-data with boundary
  const isFormData = req.body instanceof FormData;
  const headers: any = {
    'Accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };

  // Only set Content-Type for non-FormData requests
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const apiReq = req.clone({
    url: `${baseUrl}${req.url}`,
    setHeaders: headers
  });

  // 4. Unified Error Handling Pipeline
  return next(apiReq).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unexpected error occurred';

      if ([401, 403].includes(error.status)) {
        errorMessage = 'Session expired. Please login again.';
        auth.logout();
        router.navigate(['/login']);
      } else {
        errorMessage = extractErrorMessage(error.error);
      }

      if (!suppressErrorToast) {
        toast.showError(errorMessage);
      }

      return throwError(() => error);
    })
  );
};

function extractErrorMessage(errorBody: any): string {
  if (errorBody?.message) {
    return errorBody.message;
  }

  const errors = errorBody?.errors;
  if (errors && typeof errors === 'object') {
    for (const value of Object.values(errors)) {
      if (Array.isArray(value) && value.length > 0) {
        return String(value[0]);
      }

      if (typeof value === 'string' && value.trim()) {
        return value;
      }
    }
  }

  return 'An unexpected error occurred';
}
